/**
 * Admin 레이아웃: primary drawer 모바일 토글 + drilldown 스택 머신 + 헤더 드롭다운.
 *
 * Drilldown 동작:
 *   - 각 `.admin-drawer__panel` 은 단일 컬럼 안에서 transform 으로 좌우 슬라이드.
 *   - `[data-drawer-drill="<panel>"]` 클릭 → 그 패널로 push (is-active).
 *   - `[data-drawer-back]` 클릭 또는 ESC → 부모 패널로 pop.
 *   - 초기 로드 시 nav 의 `data-drawer-initial-panel` 값을 보고 그 깊이까지 애니메이션 없이 점프.
 *
 * 트리 모드(`admin.drawer.mode = tree`) 에서는 drilldown 패널이 없고 details/summary 가 직접 동작.
 */

interface DrillState {
    nav: HTMLElement;
    stack: string[];
    panels: Map<string, HTMLElement>;
}

export function initAdminLayoutDrawer(): void {
    const body       = document.getElementById("admin-body");
    const overlay    = document.getElementById("admin-drawer-overlay");
    const toggleBtns = document.querySelectorAll<HTMLElement>("[data-admin-drawer-toggle]");
    const nav        = document.getElementById("admin-drawer");

    // ── Primary drawer (모바일 토글) ───────────────────────────────────────
    function closeDrawer(): void {
        body?.classList.remove("admin--drawer-open");
        toggleBtns.forEach((btn) => {
            btn.setAttribute("aria-expanded", "false");
            btn.setAttribute("aria-label", "메뉴 열기");
        });
        overlay?.setAttribute("aria-hidden", "true");
    }

    function openDrawer(): void {
        body?.classList.add("admin--drawer-open");
        toggleBtns.forEach((btn) => {
            btn.setAttribute("aria-expanded", "true");
            btn.setAttribute("aria-label", "메뉴 닫기");
        });
        overlay?.setAttribute("aria-hidden", "false");
    }

    toggleBtns.forEach((btn) => btn.addEventListener("click", () => {
        if (body?.classList.contains("admin--drawer-open")) closeDrawer();
        else openDrawer();
    }));

    overlay?.addEventListener("click", closeDrawer);

    // ── Drilldown 스택 머신 ────────────────────────────────────────────────
    const drill = initDrilldown(nav);

    if (drill !== null) {
        // drill 버튼: 자식 패널로 push
        drill.nav.addEventListener("click", (e: Event) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            const drillBtn = target.closest<HTMLButtonElement>("[data-drawer-drill]");
            if (drillBtn) {
                const panelId = drillBtn.dataset.drawerDrill;
                if (panelId) {
                    pushPanel(drill, panelId);
                }
                return;
            }
            const backBtn = target.closest<HTMLButtonElement>("[data-drawer-back]");
            if (backBtn) {
                popPanel(drill);
            }
        });

        document.addEventListener("keydown", (e) => {
            if (e.key !== "Escape") return;
            if (drill.stack.length > 1) {
                popPanel(drill);
            } else if (body?.classList.contains("admin--drawer-open")) {
                closeDrawer();
            }
        });

        // 모바일: leaf 링크 클릭 시 primary drawer 자동 닫힘 (기존 동작)
        drill.nav.querySelectorAll<HTMLAnchorElement>("a.admin-drawer__leaf").forEach((a) => {
            a.addEventListener("click", () => {
                if (window.innerWidth <= 767) closeDrawer();
            });
        });
    }

    // ── 헤더 드롭다운 외부 클릭 닫기 ─────────────────────────────────────
    document.addEventListener("click", (e) => {
        const target = e.target as Node | null;

        const userDropdown = document.querySelector<HTMLDetailsElement>("[data-admin-user-dropdown]");
        if (userDropdown && !userDropdown.contains(target)) {
            userDropdown.removeAttribute("open");
        }

        document.querySelectorAll<HTMLDetailsElement>("[data-admin-menu-dropdown]").forEach((d) => {
            if (!d.contains(target)) d.removeAttribute("open");
        });
    });

    // 헤더 드롭다운 상호 배타
    const menuDropdowns = document.querySelectorAll<HTMLDetailsElement>("[data-admin-menu-dropdown]");
    menuDropdowns.forEach((d) => {
        d.addEventListener("toggle", () => {
            if (!d.open) return;
            menuDropdowns.forEach((other) => { if (other !== d) other.removeAttribute("open"); });
            document.querySelector<HTMLDetailsElement>("[data-admin-user-dropdown]")?.removeAttribute("open");
        });
    });
    document.querySelector<HTMLDetailsElement>("[data-admin-user-dropdown]")?.addEventListener("toggle", function () {
        if (!(this as HTMLDetailsElement).open) return;
        menuDropdowns.forEach((d) => d.removeAttribute("open"));
    });
}

// ─ Drilldown helpers ─────────────────────────────────────────────────────

/**
 * drilldown 모드 nav 초기화.
 * 트리 모드 nav 면 null 반환 (= drilldown 로직 비활성).
 */
function initDrilldown(nav: HTMLElement | null): DrillState | null {
    if (!nav || nav.dataset.drawerMode !== "drilldown") return null;

    const panels = new Map<string, HTMLElement>();
    nav.querySelectorAll<HTMLElement>(".admin-drawer__panel").forEach((p) => {
        const id = p.dataset.drawerPanel;
        if (id) panels.set(id, p);
    });
    if (panels.size === 0) return null;

    // 초기 스택 구성: data-drawer-initial-panel = "content.stores" → ['root','content','content.stores']
    const initial = nav.dataset.drawerInitialPanel ?? "root";
    const parts = initial.split(".");
    const stack: string[] = ["root"];
    if (initial !== "root") {
        for (let i = 1; i <= parts.length; i++) {
            const id = parts.slice(0, i).join(".");
            if (panels.has(id) && id !== "root") stack.push(id);
        }
    }

    const state: DrillState = { nav, stack, panels };
    // 애니메이션 없이 즉시 점프
    setActivePanel(state, stack[stack.length - 1], { animate: false });
    return state;
}

function setActivePanel(state: DrillState, target: string, opts: { animate?: boolean } = {}): void {
    const animate = opts.animate !== false;
    state.panels.forEach((p, id) => {
        const isActive = id === target;
        const isPrev = state.stack.includes(id) && id !== target;
        p.classList.toggle("is-no-animate", !animate);
        p.classList.toggle("is-active", isActive);
        p.classList.toggle("is-prev", isPrev);
        p.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
    // 다음 프레임에 is-no-animate 제거 (이후 동작은 애니메이션)
    if (!animate) {
        requestAnimationFrame(() => {
            state.panels.forEach((p) => p.classList.remove("is-no-animate"));
        });
    }
    // 활성 패널의 첫 포커스 가능 요소로 포커스 (drill/back 동작 시)
    if (animate) {
        const active = state.panels.get(target);
        const first = active?.querySelector<HTMLElement>(
            "button[data-drawer-back], button[data-drawer-drill], a.admin-drawer__leaf"
        );
        if (first) {
            // 슬라이드 후 포커스 (애니메이션 길이 직후)
            window.setTimeout(() => first.focus({ preventScroll: true }), 250);
        }
    }
}

function pushPanel(state: DrillState, panelId: string): void {
    if (!state.panels.has(panelId)) return;
    if (state.stack[state.stack.length - 1] === panelId) return;
    state.stack.push(panelId);
    setActivePanel(state, panelId);
}

function popPanel(state: DrillState): void {
    if (state.stack.length <= 1) return;
    state.stack.pop();
    const target = state.stack[state.stack.length - 1];
    // pop 은 prev → active 전환: 현재 활성 패널을 오른쪽 밖으로, 부모를 가운데로
    state.panels.forEach((p, id) => {
        const isActive = id === target;
        const isPrev = state.stack.includes(id) && !isActive;
        // 현재 active 였던 (이제 떠나는) 패널은 오른쪽으로 돌아감 — translateX(100%) 가 기본 transform
        p.classList.toggle("is-active", isActive);
        p.classList.toggle("is-prev", isPrev);
        p.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
    // 포커스 — 백 버튼 또는 첫 항목
    const active = state.panels.get(target);
    const first = active?.querySelector<HTMLElement>(
        "button[data-drawer-back], button[data-drawer-drill], a.admin-drawer__leaf"
    );
    if (first) window.setTimeout(() => first.focus({ preventScroll: true }), 250);
}
